import { Suspense } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import {
    faChair,
    faChevronDown,
    faFileExport,
    faTablePicnic,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchTableSeatingStats } from 'api/events';
import * as ExportDialog from 'assets/js/components/ExportDialog';
import { EventAttendeesTableProvider } from 'assets/js/pages/campaign-settings/campaign-event/EventAttendeesTableProvider';
import { TableSeatingStats } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/TableSeatingStats';
import { useTableSeatingIndex } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/useTableSeatingIndex';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { TableCoreResourceProvider } from 'components/table/TableCoreResourceContext';
import { useCurrentCampaignEvent } from 'customHooks/useCurrentCampaignEvent';
import TableSeatingIllustration from 'images/table-seating-splash.svg?react';
import { eventQueryKeys } from 'queries/event-query-keys';

const TableSeatingIndex = () => {
    const history = useHistory();
    const match = useRouteMatch();
    const { data: event } = useCurrentCampaignEvent();
    const { data: stats } = useSuspenseQuery({
        queryKey: eventQueryKeys.tableSeatingStats(event.id),
        queryFn: () => fetchTableSeatingStats(event.id),
    });
    const { table, search, setSearch, isPending, searchTerm } = useTableSeatingIndex();

    if (stats.seating.groups === 0) {
        return (
            <Box className={'flex flex-wrap-reverse gap-8 p-8 sm:gap-12 sm:p-12'}>
                <div className={'flex flex-1 flex-col gap-4'}>
                    <p className={'text-4xl font-medium'}>Organize your Event with Table Seating</p>
                    <div className={'flex flex-col gap-6'}>
                        <p className={'text-lg'}>
                            Create tables, assign guests, and manage your event seating! Perfect for
                            galas, banquets, and all types of events! Get started by creating your
                            first table and then build your seating plan.
                        </p>

                        <Button
                            variant="solid"
                            onClick={() => history.push(`${match.url}/builder`)}
                        >
                            Create tables
                        </Button>
                    </div>
                </div>
                <div className={'flex w-full flex-1 items-center justify-center'}>
                    <div className={'w-4/5 min-w-[100px] max-w-[350px] sm:min-w-[125px]'}>
                        <TableSeatingIllustration />
                    </div>
                </div>
            </Box>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-4">
                <span className="text-2xl font-medium">Table seating</span>
                <div className="flex gap-4">
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                            <Button color={'neutral'} variant={'outline'}>
                                Actions
                                <FontAwesomeIcon icon={faChevronDown} />
                            </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                            <DropdownMenu.Content sideOffset={4} align={'end'}>
                                <Suspense
                                    fallback={
                                        <DropdownMenu.Item className="flex gap-2" disabled>
                                            <FontAwesomeIcon icon={faFileExport} />
                                            Export Attendees
                                        </DropdownMenu.Item>
                                    }
                                >
                                    <ExportAttendeesWrapper>
                                        <ExportDialog.Root reportType="attendees">
                                            <ExportDialog.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onSelect={e => e.preventDefault()}
                                                    className="flex items-center gap-x-1.5"
                                                >
                                                    <FontAwesomeIcon icon={faFileExport} />
                                                    Export Attendees
                                                </DropdownMenu.Item>
                                            </ExportDialog.Trigger>
                                        </ExportDialog.Root>
                                    </ExportAttendeesWrapper>
                                </Suspense>
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>

                    <Button
                        variant="solid"
                        onClick={() =>
                            history.push({
                                pathname: `${match.url}/builder`,
                            })
                        }
                    >
                        <FontAwesomeIcon icon={faChair} /> Seating mode
                    </Button>
                </div>
            </div>

            <TableSeatingStats />

            <Table.Root
                isLoading={isPending}
                table={table}
                searchTerm={searchTerm}
                variant="bordered"
            >
                <Box className="flex flex-col overflow-hidden">
                    <div
                        className={
                            'flex flex-wrap items-center justify-between gap-4 border-b border-b-gray-100 p-4 md:flex-nowrap'
                        }
                    >
                        <Label className="sr-only" htmlFor="table_seating_search">
                            Search
                        </Label>
                        <Input.Root size={'sm'}>
                            <Input.SearchIcon />
                            <Input.Form
                                id="table_seating_search"
                                value={search}
                                placeholder={'Search'}
                                onChange={e => setSearch?.(e.target.value)}
                            />
                        </Input.Root>
                        <Table.ToggleFullWidth />
                    </div>
                    <Table.Content>
                        <Table.Header />
                        <Table.Body />
                    </Table.Content>
                    <Table.Empty variant={'secondary'}>
                        <Table.EmptyIcon>
                            <FontAwesomeIcon icon={faTablePicnic} aria-hidden="true" />
                        </Table.EmptyIcon>
                        <Table.EmptyTitle>No tables found</Table.EmptyTitle>
                        <Table.EmptyDescription>
                            Create tables in Seating mode
                        </Table.EmptyDescription>
                    </Table.Empty>
                    <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                        <Table.PaginationPageNavigation />
                        <Table.PaginationPerPageDropdown />
                    </Table.Pagination>
                </Box>
            </Table.Root>
        </div>
    );
};

interface ExportAttendeesWrapperProps {
    children: React.ReactNode;
}

const ExportAttendeesWrapper = ({ children }: ExportAttendeesWrapperProps) => (
    <TableCoreResourceProvider resource={'tickets'}>
        <EventAttendeesTableProvider>{children}</EventAttendeesTableProvider>
    </TableCoreResourceProvider>
);

export { TableSeatingIndex };
